import { api } from "./client";
import type { PagedResult } from "@/types/api";

/**
 * Internal Messaging v1.0 — frontend API binding.
 * Mirrors PhoenixCapital.Api.Controllers.Messaging.MessagesController.
 */

export interface MessageAttachmentDto {
  resourceType: string;
  resourceId: string;
  displayLabel: string;
  detailUrl: string;
  isDeleted: boolean;
  viewerCanAccess: boolean;
}

export interface MessageDto {
  id: string;
  conversationId: string;
  senderId: string;
  senderDisplayName: string;
  body: string;
  createdAt: string;
  readAt?: string | null;
  attachments: MessageAttachmentDto[];
}

export interface ConversationListItemDto {
  id: string;
  otherUserId: string;
  otherUserDisplayName: string;
  otherUserAvatarUrl?: string | null;
  otherUserIsActive: boolean;
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
  unreadCount: number;
  /**
   * Display label of the most recently attached record across the
   * conversation's messages (any type). Null when nothing's been attached
   * or the sender can't read the referenced record. Used by the Inbox to
   * differentiate multiple conversations with the same peer.
   */
  latestAttachmentLabel?: string | null;
}

export interface ConversationThreadDto {
  id: string;
  otherUserId: string;
  otherUserDisplayName: string;
  otherUserAvatarUrl?: string | null;
  otherUserIsActive: boolean;
  viewerIsParticipant: boolean;
  messages: MessageDto[];
}

export interface UserPickerDto {
  id: string;
  displayName: string;
  email: string;
  avatarUrl?: string | null;
  primaryRoleCode?: string | null;
}

export interface AttachmentRef {
  resourceType: string;
  resourceId: string;
}

export interface SendMessageRequest {
  recipientUserId: string;
  body: string;
  attachments?: AttachmentRef[];
  /**
   * When true, always create a fresh conversation for this pair instead of
   * appending to the existing one. Drives the composer's "Start a new
   * thread" action in the cross-context modal. Default false preserves the
   * legacy find-or-create-by-pair behaviour.
   */
  startNewThread?: boolean;
}

/** FIND-06 — existing-thread context for a peer (drives the compose warning). */
export interface PeerConversationContextDto {
  conversationId: string | null;
  contexts: MessageAttachmentDto[];
}

export const messagesApi = {
  listConversations: (page = 1, pageSize = 50) =>
    api.get<PagedResult<ConversationListItemDto>>("/messages/conversations", { page, pageSize }),
  getConversation: (id: string) =>
    api.get<ConversationThreadDto>(`/messages/conversations/${id}`),
  send: (req: SendMessageRequest) =>
    api.post<MessageDto>("/messages/conversations", req),
  poll: (id: string, after?: string) =>
    api.get<MessageDto[]>(`/messages/conversations/${id}/poll`, after ? { after } : undefined),
  unreadCount: () => api.get<number>("/messages/unread-count"),
  searchUsers: (q: string) =>
    api.get<UserPickerDto[]>("/messages/users/search", { q }),
  related: (resourceType: string, resourceId: string) =>
    api.get<ConversationListItemDto[]>(`/messages/related/${resourceType}/${resourceId}`),
  peerContext: (otherUserId: string) =>
    api.get<PeerConversationContextDto>(
      `/messages/peer-context/${encodeURIComponent(otherUserId)}`,
    ),
  resourceTypes: () => api.get<string[]>("/messages/resource-types"),
};
